import * as THREE from 'https://cdn.jsdelivr.net/npm/three@0.165.0/build/three.module.js';
/* perfect-freehand by steveruizok — turns a series of input points into a
   filled polygon outline that looks like a real ink-loaded brush stroke.
   Used by the BRUSH mode on the homepage paint board. */
import { getStroke } from 'https://esm.sh/perfect-freehand@1.2.2';

/* Convert perfect-freehand's polygon points to an SVG path "d" attribute.
   Standard helper from the library's docs — quadratic curves between
   consecutive points, closed at the end. */
function getSvgPathFromStroke(points) {
  if (!points.length) return '';
  const d = points.reduce(
    (acc, [x0, y0], i, arr) => {
      const [x1, y1] = arr[(i + 1) % arr.length];
      acc.push(x0, y0, (x0 + x1) / 2, (y0 + y1) / 2);
      return acc;
    },
    ['M', ...points[0], 'Q'],
  );
  d.push('Z');
  return d.join(' ');
}

const VERTEX_SHADER = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const FLUID_SHADER = /* glsl */ `
  uniform float iTime;
  uniform vec2  iResolution;
  uniform vec4  iMouse;
  uniform int   iFrame;
  uniform sampler2D iPreviousFrame;
  uniform float uBrushSize;
  uniform float uBrushStrength;
  uniform float uFluidDecay;
  uniform float uTrailLength;
  uniform float uPaintMode;
  varying vec2 vUv;

  vec4 sampleAt(vec2 uv) {
    return texture2D(iPreviousFrame, clamp(uv, 0.001, 0.999));
  }

  vec4 sampleOffset(vec2 uv, vec2 offsetPx) {
    return texture2D(iPreviousFrame, clamp(uv + offsetPx / iResolution, 0.001, 0.999));
  }

  float distanceToSegment(vec2 p, vec2 a, vec2 b) {
    vec2 pa = p - a;
    vec2 ba = b - a;
    float t = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
    return length(pa - ba * t);
  }

  void main() {
    vec2 uv = vUv;
    vec2 px = uv * iResolution;

    if (iFrame < 1) {
      gl_FragColor = vec4(0.0);
      return;
    }

    vec2 v = uv;
    for (int i = 0; i < 6; i++) {
      v -= sampleAt(v).xy / iResolution;
    }

    vec4 me  = sampleAt(v);
    vec4 nN  = sampleOffset(v, vec2(0.0, 1.0));
    vec4 nE  = sampleOffset(v, vec2(1.0, 0.0));
    vec4 nS  = sampleOffset(v, vec2(0.0, -1.0));
    vec4 nW  = sampleOffset(v, vec2(-1.0, 0.0));
    vec4 avg = 0.25 * (nN + nE + nS + nW);
    me = mix(me, avg, vec4(0.12, 0.12, 0.92, 0.0));

    vec2 pressureGrad = vec2(nE.z - nW.z, nN.z - nS.z);
    me.xy += pressureGrad * 60.0 / iResolution;

    me.xy *= uFluidDecay;
    me.z  *= uTrailLength;

    if (iMouse.z > 0.5) {
      vec2 mousePx     = iMouse.xy;
      vec2 mousePrevPx = iMouse.zw;
      vec2 mouseVel    = mousePx - mousePrevPx;
      float velMag     = length(mouseVel);

      float dist = distanceToSegment(px, mousePx, mousePrevPx);

      // Cubic-distance falloff: tight, localized brush (~brushSize px radius).
      float brushSizeFactor = 1e-4 / max(uBrushSize, 0.001);
      float falloff = exp(-brushSizeFactor * dist * dist * dist);
      falloff = pow(falloff, 0.5);

      vec2 dirForce = mouseVel;
      if (velMag > 1.0) dirForce = normalize(mouseVel) * min(velMag, 10.0);

      me.xy += dirForce * falloff * (0.03 * uBrushStrength);
      me.z  += falloff * (uPaintMode > 0.5 ? 1.0 : 0.4);
    }

    gl_FragColor = clamp(me, vec4(-0.4), vec4(0.4));
  }
`;

const DISPLAY_SHADER = /* glsl */ `
  uniform float iTime;
  uniform vec2  iResolution;
  uniform sampler2D iFluid;
  uniform float uDistortion;
  uniform vec3  uColorDark;
  uniform vec3  uColorMid;
  uniform vec3  uColorLight;
  uniform vec3  uColorWhite;
  uniform vec3  uAccent;
  varying vec2 vUv;

  vec2 warp(vec2 uv, float t) {
    float a = 0.0;
    float d = -t * 0.05;
    for (float i = 0.0; i < 6.0; ++i) {
      a += cos(i - d - a * uv.x);
      d += sin(uv.y * i + a);
    }
    return vec2(a, d);
  }

  void main() {
    vec2 uv = (vUv * 2.0 - 1.0);
    uv.x *= iResolution.x / iResolution.y;

    vec4 fluid    = texture2D(iFluid, vUv);
    vec2 fluidVel = fluid.xy;
    float trail   = clamp(fluid.z * 1.5, 0.0, 1.0);

    uv += fluidVel * uDistortion * 0.5;

    float t = iTime * 0.18;
    vec2  w  = warp(uv * 1.4, t);
    float bands = sin(w.x * 1.6 + w.y * 0.8 + t * 0.4) * 0.5 + 0.5;
    float fine  = sin(w.y * 3.2 - t * 0.6) * 0.5 + 0.5;

    float radial = length(uv) * 0.4;
    float lum = clamp(bands * 0.7 + fine * 0.3 + radial * 0.05, 0.0, 1.0);

    float rim = pow(abs(sin(w.x + w.y * 0.5)), 6.0);
    lum = clamp(lum + rim * 0.35, 0.0, 1.0);

    vec3 col;
    if (lum < 0.33) {
      col = mix(uColorDark, uColorMid, lum / 0.33);
    } else if (lum < 0.78) {
      col = mix(uColorMid, uColorLight, (lum - 0.33) / 0.45);
    } else {
      col = mix(uColorLight, uColorWhite, (lum - 0.78) / 0.22);
    }

    col = mix(col, uColorWhite, trail * 0.25);

    /* Theme-accent glow: gentle wash across the whole canvas with a slight
       brighter centre.  Pure white accent is identity (no perceived change);
       coloured accent reads as a coloured light source. */
    float glow = mix(0.65, 1.0, smoothstep(2.4, 0.1, length(uv)));
    vec3  tinted = uAccent * (lum * 1.05 + 0.10);
    float tintAmt = (lum * 0.6 + 0.4) * glow * 0.38;
    col = mix(col, tinted, tintAmt);

    /* Edge vignette: darken top + bottom strips so floating white text
       reads cleanly without needing text-shadow. */
    float topVig = smoothstep(0.78, 1.00, vUv.y);
    float botVig = smoothstep(0.20, 0.00, vUv.y);
    float vig = max(topVig, botVig);
    col = mix(col, col * 0.45, vig * 0.85);

    gl_FragColor = vec4(col, 1.0);
  }
`;

function hexToVec3(hex) {
  const c = hex.replace('#', '');
  const v = c.length === 3
    ? c.split('').map((ch) => parseInt(ch + ch, 16))
    : [c.slice(0, 2), c.slice(2, 4), c.slice(4, 6)].map((s) => parseInt(s, 16));
  return new THREE.Vector3(v[0] / 255, v[1] / 255, v[2] / 255);
}

class OpqHero {
  constructor(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-opq-canvas]');
    if (!this.canvas) return;

    const ds = root.dataset;
    this.config = {
      brushSize:     parseFloat(ds.brushSize)     || 25,
      brushStrength: parseFloat(ds.brushStrength) || 10,
      fluidDecay:    parseFloat(ds.fluidDecay)    || 0.985,
      trailLength:   parseFloat(ds.trailLength)   || 0.85,
      distortion:    parseFloat(ds.distortion)    || 1.2,
      colorDark:     hexToVec3(ds.colorDark  || '#000000'),
      colorMid:      hexToVec3(ds.colorMid   || '#3a3a3a'),
      colorLight:    hexToVec3(ds.colorLight || '#bdbdbd'),
      colorWhite:    hexToVec3(ds.colorWhite || '#ffffff'),
    };

    this.mouse = { x: 0, y: 0, prevX: 0, prevY: 0, active: 0 };
    this.frame = 0;
    this.running = false;
    this.dpr = Math.min(window.devicePixelRatio || 1, 1.5);

    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.init();
  }

  init() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: false,
      alpha: false,
      powerPreference: 'high-performance',
      /* required so drawImage(webglCanvas) works during Export */
      preserveDrawingBuffer: true,
    });
    this.renderer.setPixelRatio(this.dpr);

    const rtOptions = {
      type: THREE.HalfFloatType,
      magFilter: THREE.LinearFilter,
      minFilter: THREE.LinearFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
      depthBuffer: false,
      stencilBuffer: false,
    };
    this.rtA = new THREE.WebGLRenderTarget(1, 1, rtOptions);
    this.rtB = new THREE.WebGLRenderTarget(1, 1, rtOptions);

    this.geom = new THREE.PlaneGeometry(2, 2);

    this.fluidMaterial = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: FLUID_SHADER,
      uniforms: {
        iTime:          { value: 0 },
        iResolution:    { value: new THREE.Vector2(1, 1) },
        iMouse:         { value: new THREE.Vector4(0, 0, 0, 0) },
        iFrame:         { value: 0 },
        iPreviousFrame: { value: this.rtA.texture },
        uBrushSize:     { value: this.config.brushSize },
        uBrushStrength: { value: this.config.brushStrength },
        uFluidDecay:    { value: this.config.fluidDecay },
        uTrailLength:   { value: this.config.trailLength },
        uPaintMode:     { value: 0 },
      },
    });

    this.displayMaterial = new THREE.ShaderMaterial({
      vertexShader: VERTEX_SHADER,
      fragmentShader: DISPLAY_SHADER,
      uniforms: {
        iTime:       { value: 0 },
        iResolution: { value: new THREE.Vector2(1, 1) },
        iFluid:      { value: this.rtA.texture },
        uDistortion: { value: this.config.distortion },
        uColorDark:  { value: this.config.colorDark },
        uColorMid:   { value: this.config.colorMid },
        uColorLight: { value: this.config.colorLight },
        uColorWhite: { value: this.config.colorWhite },
        uAccent:     { value: new THREE.Vector3(1, 1, 1) },
      },
    });

    this.fluidMesh = new THREE.Mesh(this.geom, this.fluidMaterial);
    this.displayMesh = new THREE.Mesh(this.geom, this.displayMaterial);

    this.handleResize = this.handleResize.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerLeave = this.handlePointerLeave.bind(this);
    this.tick = this.tick.bind(this);

    window.addEventListener('resize', this.handleResize);
    window.addEventListener('pointermove', this.handlePointerMove, { passive: true });
    window.addEventListener('pointerleave', this.handlePointerLeave, { passive: true });

    this.handleResize();

    if (this.reducedMotion) {
      this.scene.add(this.displayMesh);
      this.displayMaterial.uniforms.iFluid.value = this.rtA.texture;
      this.renderer.setRenderTarget(null);
      this.renderer.render(this.scene, this.camera);
      return;
    }

    this.observer = new IntersectionObserver((entries) => {
      const visible = entries[0]?.isIntersecting;
      if (visible) this.start();
      else this.stop();
    }, { threshold: 0 });
    this.observer.observe(this.root);

    this.start();
  }

  handleResize() {
    const w = this.canvas.clientWidth || window.innerWidth;
    const h = this.canvas.clientHeight || window.innerHeight;
    this.renderer.setSize(w, h, false);

    const rtW = Math.floor(w * this.dpr);
    const rtH = Math.floor(h * this.dpr);
    this.rtA.setSize(rtW, rtH);
    this.rtB.setSize(rtW, rtH);

    this.fluidMaterial.uniforms.iResolution.value.set(rtW, rtH);
    this.displayMaterial.uniforms.iResolution.value.set(rtW, rtH);

    this.frame = 0;
  }

  handlePointerMove(e) {
    const rect = this.canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) * this.dpr;
    const y = (rect.height - (e.clientY - rect.top)) * this.dpr;
    this.mouse.prevX = this.mouse.x;
    this.mouse.prevY = this.mouse.y;
    this.mouse.x = x;
    this.mouse.y = y;
    this.mouse.active = 1;
  }

  handlePointerLeave() {
    this.mouse.active = 0;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.lastTime = performance.now();
    this.tick();
  }

  stop() {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
  }

  tick(now = performance.now()) {
    if (!this.running) return;
    this.rafId = requestAnimationFrame(this.tick);

    const t = now * 0.001;

    const u = this.fluidMaterial.uniforms;
    u.iTime.value  = t;
    u.iFrame.value = this.frame;
    u.iMouse.value.set(this.mouse.x, this.mouse.y, this.mouse.prevX, this.mouse.prevY);
    u.iPreviousFrame.value = this.rtA.texture;

    this.scene.clear();
    this.scene.add(this.fluidMesh);
    this.renderer.setRenderTarget(this.rtB);
    this.renderer.render(this.scene, this.camera);

    this.displayMaterial.uniforms.iTime.value = t;
    this.displayMaterial.uniforms.iFluid.value = this.rtB.texture;
    this.scene.clear();
    this.scene.add(this.displayMesh);
    this.renderer.setRenderTarget(null);
    this.renderer.render(this.scene, this.camera);

    const tmp = this.rtA;
    this.rtA = this.rtB;
    this.rtB = tmp;

    this.mouse.prevX = this.mouse.x;
    this.mouse.prevY = this.mouse.y;
    if (this.mouse.active) this.mouse.active = Math.max(0, this.mouse.active - 0.05);

    this.frame++;
  }

  destroy() {
    this.stop();
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('pointermove', this.handlePointerMove);
    window.removeEventListener('pointerleave', this.handlePointerLeave);
    this.observer?.disconnect();
    this.rtA.dispose();
    this.rtB.dispose();
    this.geom.dispose();
    this.fluidMaterial.dispose();
    this.displayMaterial.dispose();
    this.renderer.dispose();
  }
}

const SETTINGS_KEY = 'opq-hero:settings';

function readSettings() {
  try {
    return JSON.parse(localStorage.getItem(SETTINGS_KEY)) || {};
  } catch {
    return {};
  }
}

function writeSettings(patch) {
  const next = { ...readSettings(), ...patch };
  try { localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch {}
  return next;
}

function initControls(root) {
  const themeBtn   = root.querySelector('[data-opq-ctrl="theme"]');
  const brushBtn   = root.querySelector('[data-opq-ctrl="brush"]');
  const langBtn    = root.querySelector('[data-opq-ctrl="lang"]');
  const colorIn    = root.querySelector('[data-opq-color-input]');
  const brushIn    = root.querySelector('[data-opq-brush-input]');
  const langLabel  = root.querySelector('[data-opq-lang-label]');

  const settings = readSettings();

  /* Brush color is stored per-section so the homepage (white BG → black brush)
     and the hero-board page (dark BG → white brush) keep their own preferences.
     Other settings (accent, lang) stay shared. */
  const brushSection = root.classList.contains('opq-board') ? 'board'
    : root.classList.contains('opq-hero') ? 'hero'
    : 'default';
  const brushKey = `brushColor_${brushSection}`;

  // Theme accent color — UI accent var + soft tint on the chrome shader
  const applyAccent = (color) => {
    if (!color) return;
    root.style.setProperty('--opq-accent', color);
    if (colorIn) colorIn.value = color;
    const hero = root.__opqHero;
    if (hero?.displayMaterial) {
      const v = hexToVec3(color);
      hero.displayMaterial.uniforms.uAccent.value.copy(v);
    }
  };
  applyAccent(settings.accent || '#ffffff');
  /* Input is now full-size on top with pointer-events: auto, so taps land
     directly on the colour input. Guard against double-fire when the
     button itself receives a click (e.g. keyboard Enter). */
  themeBtn?.addEventListener('click', (e) => {
    if (e.target === colorIn) return;
    colorIn?.click();
  });
  colorIn?.addEventListener('input', (e) => {
    applyAccent(e.target.value);
    writeSettings({ accent: e.target.value });
  });

  // Brush color (paint strokes only — does NOT affect UI accent)
  const applyBrush = (color) => {
    if (!color) return;
    root.style.setProperty('--opq-brush-color', color);
    if (brushIn) brushIn.value = color;
  };
  /* Section can declare its own default brush colour via data-opq-default-brush
     (e.g. opq-board uses #000000 since its BG is white). */
  applyBrush(settings[brushKey] || root.dataset.opqDefaultBrush || '#ffffff');
  brushBtn?.addEventListener('click', (e) => {
    if (e.target === brushIn) return;
    brushIn?.click();
  });
  brushIn?.addEventListener('input', (e) => {
    applyBrush(e.target.value);
    writeSettings({ [brushKey]: e.target.value });
  });

  // Language toggle (visual only for now — Phase 2 will wire i18n)
  const applyLang = (lang) => {
    if (langLabel) langLabel.textContent = lang === 'cn' ? 'CN' : 'EN';
    if (langBtn)   langBtn.classList.toggle('is-active', lang === 'cn');
    root.dataset.lang = lang;
  };
  applyLang(settings.lang === 'cn' ? 'cn' : 'en');
  langBtn?.addEventListener('click', () => {
    const next = root.dataset.lang === 'cn' ? 'en' : 'cn';
    applyLang(next);
    writeSettings({ lang: next });
  });
}

function generateSmoothPath(points) {
  if (points.length === 0) return '';
  if (points.length === 1) return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  if (points.length === 2) {
    return `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)} L ${points[1].x.toFixed(1)} ${points[1].y.toFixed(1)}`;
  }

  let path = `M ${points[0].x.toFixed(1)} ${points[0].y.toFixed(1)}`;
  for (let i = 0; i < points.length - 1; i++) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;

    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;

    path += ` C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)} ${cp2x.toFixed(1)} ${cp2y.toFixed(1)} ${p2.x.toFixed(1)} ${p2.y.toFixed(1)}`;
  }
  return path;
}

function initPaint(root) {
  const svg = root.querySelector('[data-opq-paint-svg]');
  const completed = root.querySelector('[data-opq-paint-completed]');
  const modeText = root.querySelector('[data-opq-mode]');
  const clearBtn = root.querySelector('[data-opq-ctrl="clear"]');
  const lineBtn  = root.querySelector('[data-opq-ctrl="line"]');
  const dock = root.querySelector('[data-opq-dock]');
  if (!svg || !completed) return;

  /* Straight-line mode: when on, a press-drag-release renders a single
     ruled line from press point to release point instead of a freehand
     stroke. Toggled via the line button. */
  let lineMode = false;
  let lineStart = null;

  const SAFE_X_INSET = 0.15;
  const SAFE_Y_INSET = 0.20;
  const SPEED_SMOOTH = 0.55;
  const MIN_W = 2;
  const MAX_W = 14;
  const PEN_W = 4;

  /* Real-brush variable width: split each held-down stroke into many short
     segment paths, each with its own stroke-width. Adjacent segments share
     the joining point so the visual is continuous. */
  const SEG_MAX_POINTS = 5;

  /* perfect-freehand options for the BRUSH (毛笔/书法) mode. Values picked
     to feel like a soft ink brush: thinning=0.7 makes fast strokes
     pinstripe-thin and slow strokes load up wide; taper at both ends gives
     calligraphic head/tail; streamline=0.5 smooths jitter without lagging. */
  const BRUSH_OPTIONS = {
    size: 22,
    thinning: 0.7,
    smoothing: 0.5,
    streamline: 0.5,
    easing: (t) => t,
    start: { taper: 35, cap: true },
    end:   { taper: 30, cap: true },
    simulatePressure: true,
  };

  /* Brush style — switched via the brush-mode popover on the board page.
       pen    : fixed 4px width, single path, no smoothing math (cleanest)
       smooth : variable width by speed (current default for hero)
       brush  : perfect-freehand polygon — true 毛筆 / calligraphy feel,
                tapered head + tail, velocity-driven pressure */
  let brushMode = root.dataset.opqDefaultBrushMode || 'smooth';

  /* Per-mode size — each mode keeps its own size so the SIZE slider feels
     "remembered" across mode switches (PEN at 4 doesn't ruin a 22-size
     BRUSH stroke after switching back). */
  const brushSizes = { pen: PEN_W, smooth: MAX_W, brush: 22 };

  /* Stroke opacity (0–1). Applied as the SVG `opacity` attribute on each
     new path at creation time, so changing the slider mid-stroke only
     affects subsequent strokes (consistent visual once you let go). */
  let brushOpacity = 1.0;

  /* perfect-freehand state — only used when brushMode === 'brush' */
  let brushPolyPath = null;
  let brushRawPoints = [];

  const inSafeZone = (x, y) => {
    const w = window.innerWidth, h = window.innerHeight;
    /* Board page: drawable zone is the full viewport minus the actual top
       header strip and the bottom GALLERY/SHOP/CONTACT/TERMS row. UI buttons
       inside those strips are already excluded by isUITarget; this just
       widens the open canvas area beyond the central safe-zone used by the
       fluid hero. */
    if (root.classList.contains('opq-board')) {
      const topEl = root.querySelector('.opq-hero__top');
      const bottomEl = root.querySelector('.opq-board__bottom-bar');
      const PAD = 6;
      const topY = topEl ? topEl.getBoundingClientRect().bottom + PAD : 0;
      const bottomY = bottomEl ? bottomEl.getBoundingClientRect().top - PAD : h;
      return x >= 0 && x <= w && y >= topY && y <= bottomY;
    }
    return x >= w * SAFE_X_INSET && x <= w * (1 - SAFE_X_INSET)
        && y >= h * SAFE_Y_INSET && y <= h * (1 - SAFE_Y_INSET);
  };

  const isUITarget = (target) => {
    if (!target || typeof target.closest !== 'function') return false;
    return !!target.closest('a, button, input, .opq-hero__nav, .opq-hero__dock, .opq-hero__top-right, .opq-hero__brand, .opq-hero__footer-nav, .opq-hero__signature');
  };

  /* Speed → stroke width for SMOOTH mode. Scales the linear factor with
     the current max so doubling SIZE doesn't squash the dynamic range. */
  const speedToWidth = (speed) => {
    const max = brushSizes.smooth;
    const min = Math.max(0.8, max * 0.14);
    return Math.max(min, Math.min(max, max - speed * (max * 0.12)));
  };

  let pressed = false;
  let segPath = null;          // currently growing SVG <path>
  let segPoints = [];          // points in the current segment
  let lastTime = 0;
  let smoothSpeed = 4;
  let lastX = 0, lastY = 0;

  /* Event log used by Export to replay strokes 1:1. Cleared by Clear. */
  root.__opqPaintRecording = [];
  const recording = root.__opqPaintRecording;

  const setPaintFluidBoost = (on) => {
    const hero = root.__opqHero;
    if (!hero) return;
    const u = hero.fluidMaterial.uniforms;
    /* Paint mode:
       - brush instantly shrinks to 1/3 of the hover size (precise tip)
       - strength keeps same force-per-pixel so the small area isn't overpowered
       - decay near 1.0 so the impression lingers after the cursor moves on  */
    u.uBrushSize.value     = on ? hero.config.brushSize * 0.33 : hero.config.brushSize;
    u.uBrushStrength.value = hero.config.brushStrength;
    u.uFluidDecay.value    = on ? 0.99                          : hero.config.fluidDecay;
    u.uTrailLength.value   = on ? 0.95                          : hero.config.trailLength;
    u.uPaintMode.value     = on ? 1                             : 0;
  };

  const startSegment = (firstPoint, width) => {
    segPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    segPath.setAttribute('class', 'opq-paint-stroke');
    segPath.setAttribute('stroke-width', width.toFixed(2));
    segPath.setAttribute('d', `M ${firstPoint.x.toFixed(1)} ${firstPoint.y.toFixed(1)}`);
    if (brushOpacity < 1) segPath.style.opacity = brushOpacity.toFixed(2);
    completed.appendChild(segPath);
    segPoints = [firstPoint];
  };

  const onDown = (e) => {
    if (e.button !== 0) return;
    if (isUITarget(e.target)) return;
    if (!inSafeZone(e.clientX, e.clientY)) return;

    pressed = true;
    lastTime = performance.now();
    smoothSpeed = 4;

    if (modeText) modeText.textContent = lineMode ? 'Line' : 'Paint';
    root.classList.add('is-painting');
    setPaintFluidBoost(true);

    /* Lock dock visible while painting (until user clicks any control) */
    dock?.classList.add('is-visible', 'is-locked');

    lastX = e.clientX; lastY = e.clientY;
    recording.push({ type: 'down', t: performance.now(), x: e.clientX, y: e.clientY });

    if (lineMode) {
      lineStart = { x: e.clientX, y: e.clientY };
      const lineW = brushMode === 'pen' ? brushSizes.pen : brushSizes.smooth * 0.5;
      startSegment(lineStart, lineW);
    } else if (brushMode === 'brush') {
      /* perfect-freehand: render a filled polygon outline that accumulates
         all input points and is regenerated on every move. */
      brushPolyPath = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      brushPolyPath.setAttribute('class', 'opq-paint-stroke opq-paint-stroke--brush');
      if (brushOpacity < 1) brushPolyPath.style.opacity = brushOpacity.toFixed(2);
      completed.appendChild(brushPolyPath);
      brushRawPoints = [[e.clientX, e.clientY, e.pressure || 0.5]];
      /* Mark "active path" so onMove's `!segPath` guard passes. */
      segPath = brushPolyPath;
      segPoints = [];
    } else {
      const initW = brushMode === 'pen' ? brushSizes.pen : brushSizes.smooth * 0.6;
      startSegment({ x: e.clientX, y: e.clientY }, initW);
    }
  };

  const onMove = (e) => {
    if (!pressed || !segPath) return;

    /* Line mode: redraw the single segment as a straight line from
       press-point to current pointer. No smoothing, no segmentation. */
    if (lineMode && lineStart) {
      lastX = e.clientX; lastY = e.clientY;
      segPath.setAttribute(
        'd',
        `M ${lineStart.x.toFixed(1)} ${lineStart.y.toFixed(1)} L ${e.clientX.toFixed(1)} ${e.clientY.toFixed(1)}`
      );
      return;
    }

    /* Brush mode: doesn't share segPoints/segPath state — handle entirely
       separately before the segment-based distance/width math. */
    if (brushMode === 'brush' && brushPolyPath) {
      const dx = e.clientX - lastX;
      const dy = e.clientY - lastY;
      if (dx * dx + dy * dy < 1.4) return;
      lastX = e.clientX; lastY = e.clientY;
      lastTime = performance.now();
      recording.push({ type: 'move', t: lastTime, x: e.clientX, y: e.clientY });
      brushRawPoints.push([e.clientX, e.clientY, e.pressure || 0.5]);
      const outline = getStroke(brushRawPoints, { ...BRUSH_OPTIONS, size: brushSizes.brush });
      brushPolyPath.setAttribute('d', getSvgPathFromStroke(outline));
      return;
    }

    const now = performance.now();
    const last = segPoints[segPoints.length - 1];
    const dx = e.clientX - last.x;
    const dy = e.clientY - last.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1.2) return;

    const newPoint = { x: e.clientX, y: e.clientY };
    lastX = newPoint.x; lastY = newPoint.y;
    recording.push({ type: 'move', t: performance.now(), x: newPoint.x, y: newPoint.y });

    /* Pen mode: one continuous path, fixed width, no segmentation math. */
    if (brushMode === 'pen') {
      lastTime = now;
      segPoints.push(newPoint);
      segPath.setAttribute('d', generateSmoothPath(segPoints));
      return;
    }

    const dt = Math.max(now - lastTime, 1);
    lastTime = now;
    const speed = dist / dt;
    smoothSpeed = smoothSpeed * SPEED_SMOOTH + speed * (1 - SPEED_SMOOTH);
    const newWidth = speedToWidth(smoothSpeed);

    segPoints.push(newPoint);
    segPath.setAttribute('d', generateSmoothPath(segPoints));

    /* Split segment when it has enough points — start a fresh path with the
       latest point as its start, so the next segment can have a different
       width. Adjacent segments overlap on the joining point for continuity. */
    if (segPoints.length >= SEG_MAX_POINTS) {
      startSegment(newPoint, newWidth);
    }
  };

  const onUp = () => {
    if (!pressed) return;
    pressed = false;

    /* Line mode: record final endpoint into the export log so replays
       reconstruct the line as a 2-point segment. */
    if (lineMode && lineStart) {
      recording.push({ type: 'move', t: performance.now(), x: lastX, y: lastY });
      lineStart = null;
    }

    segPath = null;
    segPoints = [];
    brushPolyPath = null;
    brushRawPoints = [];

    recording.push({ type: 'up', t: performance.now(), x: lastX, y: lastY });

    if (modeText) {
      const isBoard = root.classList.contains('opq-board');
      modeText.textContent = isBoard
        ? (lineMode ? 'Line' : (brushMode === 'smooth' ? 'Smooth' : brushMode === 'brush' ? 'Brush' : 'Pen'))
        : 'Select';
    }
    root.classList.remove('is-painting');
    setPaintFluidBoost(false);
    /* dock stays locked-visible until the user clicks a control button */
  };

  const clearAll = () => {
    while (completed.firstChild) completed.removeChild(completed.firstChild);
    segPath = null;
    segPoints = [];
    recording.length = 0;
  };

  document.addEventListener('pointerdown', onDown);
  document.addEventListener('pointermove', onMove, { passive: true });
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', onUp);

  clearBtn?.addEventListener('click', clearAll);

  /* Line-mode toggle. The button itself is in the dock so clicking it
     bubbles to the unlock handler below; we don't unlock here because the
     user is likely about to draw a line and wants the dock to stay. */
  lineBtn?.addEventListener('click', () => {
    lineMode = !lineMode;
    lineBtn.classList.toggle('is-active', lineMode);
    lineBtn.setAttribute('aria-pressed', lineMode ? 'true' : 'false');
    if (modeText && !pressed) modeText.textContent = lineMode ? 'Line' : brushModeLabel(brushMode);
  });

  /* Brush-style popover (PEN / SMOOTH / INK). Click button toggles popover;
     click an option sets brushMode and closes popover; click outside closes. */
  const brushModeBtn = root.querySelector('[data-opq-ctrl="brush-mode"]');
  const brushPopover = root.querySelector('[data-opq-brush-popover]');
  const brushModeLabel = (m) => m === 'smooth' ? 'Smooth' : m === 'brush' ? 'Brush' : 'Pen';

  if (brushModeBtn && brushPopover) {
    /* Sync initial active option to default mode */
    brushPopover.querySelectorAll('[data-opq-brush-mode]').forEach((opt) => {
      opt.classList.toggle('is-active', opt.dataset.opqBrushMode === brushMode);
    });
    if (modeText && !pressed) modeText.textContent = brushModeLabel(brushMode);

    /* SIZE slider — slider value reflects the current mode's stored size.
       Switching modes loads that mode's size; dragging slider writes back. */
    const sizeInput  = root.querySelector('[data-opq-brush-size]');
    const sizeReadout = root.querySelector('[data-opq-brush-size-value]');
    const syncSizeUI = () => {
      const v = brushSizes[brushMode];
      if (sizeInput)   sizeInput.value = String(v);
      if (sizeReadout) sizeReadout.textContent = String(v);
    };
    syncSizeUI();
    sizeInput?.addEventListener('input', (e) => {
      const v = Math.max(1, Math.min(40, parseInt(e.target.value, 10) || 1));
      brushSizes[brushMode] = v;
      if (sizeReadout) sizeReadout.textContent = String(v);
    });

    /* OPACITY slider — global (not per-mode); written into each new path's
       `opacity` attribute on creation. */
    const opacityInput   = root.querySelector('[data-opq-brush-opacity]');
    const opacityReadout = root.querySelector('[data-opq-brush-opacity-value]');
    opacityInput?.addEventListener('input', (e) => {
      const pct = Math.max(5, Math.min(100, parseInt(e.target.value, 10) || 100));
      brushOpacity = pct / 100;
      if (opacityReadout) opacityReadout.textContent = String(pct);
    });

    /* On desktop the popover opens via CSS hover, so click-toggle would
       conflict (clicking would "pin" it open after the user moves away).
       Only run the click-toggle on touch / no-hover devices. */
    const isHoverCapable = typeof window.matchMedia === 'function'
      && window.matchMedia('(hover: hover)').matches;
    brushModeBtn.addEventListener('click', (e) => {
      if (isHoverCapable) {
        /* Still prevent the dock-unlock from running while the user might
           be about to interact with the hover popover. */
        e.stopImmediatePropagation();
        return;
      }
      e.stopImmediatePropagation();
      const open = brushPopover.classList.toggle('is-open');
      brushModeBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (open) dock?.classList.add('is-visible', 'is-locked');
    });

    brushPopover.querySelectorAll('[data-opq-brush-mode]').forEach((opt) => {
      opt.addEventListener('click', (e) => {
        e.stopPropagation();
        brushMode = opt.dataset.opqBrushMode;
        brushPopover.querySelectorAll('[data-opq-brush-mode]').forEach((o) => {
          o.classList.toggle('is-active', o === opt);
        });
        brushPopover.classList.remove('is-open');
        brushModeBtn.setAttribute('aria-expanded', 'false');
        if (modeText && !pressed && !lineMode) modeText.textContent = brushModeLabel(brushMode);
        syncSizeUI();
      });
    });

    /* Click outside closes popover */
    document.addEventListener('pointerdown', (e) => {
      if (!brushPopover.classList.contains('is-open')) return;
      if (e.target.closest('[data-opq-brush-mode-wrap]')) return;
      brushPopover.classList.remove('is-open');
      brushModeBtn.setAttribute('aria-expanded', 'false');
    });
  }

  /* Any control click unlocks the dock so it can hide normally again. */
  root.querySelectorAll('[data-opq-ctrl]').forEach((btn) => {
    btn.addEventListener('click', () => dock?.classList.remove('is-locked'));
  });
}

/* Render a single DOM text element onto the export canvas at its live
   position / font / colour, scaled by sx / sy.  Shared by video + image
   exports so any CSS change is automatically reflected in the saved file. */
function drawDomText(ctx, el, sx, sy) {
  const rect = el.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return;
  const cs = getComputedStyle(el);
  const fontPx = parseFloat(cs.fontSize) * Math.min(sx, sy);
  if (!Number.isFinite(fontPx) || fontPx < 1) return;

  const family = cs.fontFamily || '"Helvetica Neue", Helvetica, Arial, sans-serif';
  ctx.font = `${cs.fontStyle} ${cs.fontWeight} ${Math.round(fontPx)}px ${family}`;
  ctx.fillStyle = cs.color || '#fff';
  ctx.textBaseline = 'top';

  const text = (el.textContent || '').trim();
  if (!text) return;

  const x = rect.left * sx;
  const y = rect.top * sy;
  const w = rect.width * sx;
  const align = cs.textAlign;

  if (align === 'right' || align === 'end') {
    ctx.textAlign = 'right';
    ctx.fillText(text, x + w, y);
  } else if (align === 'center') {
    ctx.textAlign = 'center';
    ctx.fillText(text, x + w / 2, y);
  } else {
    ctx.textAlign = 'left';
    ctx.fillText(text, x, y);
  }
}

/* Composite the section's overlay chrome (logo, status, meta, footer-nav,
   copyright, MENU) onto the export canvas.  Selectors cover both hero and
   board class names so the same routine works on every section that has the
   shared top header. */
function drawOverlayUI(ctx, root, sx, sy) {
  const logo = root.querySelector('.opq-hero__logo');
  if (logo) drawDomText(ctx, logo, sx, sy);

  /* Homepage (opq-board): export captures only the OPQ logo + paint canvas.
     The cursor/mode/location/time/MENU/COLOR/footer/©  are studio chrome
     and shouldn't bleed into the user's saved artwork. */
  if (root.classList.contains('opq-board')) return;

  /* Status, meta — only present on hero / board snippets */
  root.querySelectorAll('.opq-hero__status p').forEach((p) => drawDomText(ctx, p, sx, sy));
  root.querySelectorAll('.opq-hero__meta p').forEach((p) => drawDomText(ctx, p, sx, sy));

  /* Footer nav: hero uses .opq-hero__footer-nav; board uses .opq-board__footer-nav */
  root.querySelectorAll('.opq-hero__footer-nav a, .opq-board__footer-nav a').forEach(
    (a) => drawDomText(ctx, a, sx, sy)
  );

  /* Copyright (board) — single <p> with hyperlink for "Terms" */
  const copyright = root.querySelector('.opq-hero__copyright, .opq-board__copyright');
  if (copyright) drawDomText(ctx, copyright, sx, sy);

  /* COLOR picker label (board only) */
  const colorBtn = root.querySelector('.opq-board__bg-picker');
  if (colorBtn) drawDomText(ctx, colorBtn, sx, sy);

  /* MENU button — render pill background then centred text */
  const menu = root.querySelector('.opq-hero__menu');
  if (menu) {
    const rect = menu.getBoundingClientRect();
    if (rect.width > 0 && rect.height > 0) {
      const x = rect.left * sx;
      const y = rect.top * sy;
      const w = rect.width * sx;
      const h = rect.height * sy;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(x, y, w, h, h / 2);
      else ctx.rect(x, y, w, h);
      const menuCs = getComputedStyle(menu);
      ctx.fillStyle = menuCs.backgroundColor || 'rgba(0, 0, 0, 0.10)';
      ctx.fill();
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.20)';
      ctx.lineWidth = 0.5;
      ctx.stroke();

      const fontPx = parseFloat(menuCs.fontSize) * Math.min(sx, sy);
      const family = menuCs.fontFamily || '"Helvetica Neue", Helvetica, Arial, sans-serif';
      ctx.font = `${menuCs.fontWeight} ${Math.round(fontPx)}px ${family}`;
      ctx.fillStyle = menuCs.color || '#000';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText((menu.textContent || 'MENU').trim(), x + w / 2, y + h / 2);
    }
  }
}

function initExport(root) {
  const exportBtn = root.querySelector('[data-opq-ctrl="export"]');
  const webglCanvas = root.querySelector('[data-opq-canvas]');
  const svg = root.querySelector('[data-opq-paint-svg]');
  const completed = root.querySelector('[data-opq-paint-completed]');
  const modeText = root.querySelector('[data-opq-mode]');
  if (!exportBtn) return;

  /* Compress idle gaps between strokes (gap > 300ms → 80ms) so the video
     stays compact instead of including dead air. */
  const compressTiming = (events) => {
    if (events.length === 0) return [];
    const out = events.map((e) => ({ ...e, replayT: 0 }));
    let elapsed = 0;
    out[0].replayT = 0;
    for (let i = 1; i < out.length; i++) {
      const dt = out[i].t - out[i - 1].t;
      const compressed = dt > 300 ? 80 : dt;
      elapsed += compressed;
      out[i].replayT = elapsed;
    }
    return out;
  };

  const setBusy = (on) => {
    exportBtn.classList.toggle('is-busy', on);
    exportBtn.disabled = on;
  };

  /* Capture-phase blocker that swallows the real user's mouse during export
     so the replay isn't contaminated by their live cursor.
     Synthetic events from the export get tagged __opqSynthetic and pass through. */
  const POINTER_TYPES = ['pointerdown', 'pointermove', 'pointerup', 'pointercancel'];
  const blockReal = (e) => {
    if (!e.__opqSynthetic) {
      e.stopPropagation();
      e.stopImmediatePropagation();
    }
  };
  const installInputLock = () => {
    POINTER_TYPES.forEach((t) => {
      window.addEventListener(t, blockReal, true);
      document.addEventListener(t, blockReal, true);
    });
  };
  const removeInputLock = () => {
    POINTER_TYPES.forEach((t) => {
      window.removeEventListener(t, blockReal, true);
      document.removeEventListener(t, blockReal, true);
    });
  };

  const runExport = async () => {
    const recording = root.__opqPaintRecording || [];
    if (recording.length === 0) {
      alert('No paint to export. Try painting something first!');
      return;
    }
    setBusy(true);

    const events = compressTiming(recording);
    const totalDuration = events[events.length - 1].replayT;

    /* Composite output canvas — fixed 854x480 per spec */
    const W = 854, H = 480;
    const compCanvas = document.createElement('canvas');
    compCanvas.width = W;
    compCanvas.height = H;
    const ctx = compCanvas.getContext('2d');

    const sx = W / window.innerWidth;
    const sy = H / window.innerHeight;

    /* Background colour used when there's no WebGL chrome canvas (e.g. on
       the white-bg board).  Read once before the recorder starts. */
    const compositeBgColor = getComputedStyle(root).backgroundColor || '#ffffff';

    /* MediaRecorder on canvas-captured stream */
    const stream = compCanvas.captureStream(30);
    const mime = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : 'video/webm';
    const recorder = new MediaRecorder(stream, { mimeType: mime, videoBitsPerSecond: 4_000_000 });
    const chunks = [];
    recorder.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunks.push(e.data); };

    /* Lock real user input out for the duration of the replay */
    installInputLock();

    /* Clear current strokes before replay */
    while (completed.firstChild) completed.removeChild(completed.firstChild);

    /* Reset the fluid simulation buffer so chrome starts clean. */
    const hero = root.__opqHero;
    if (hero) hero.frame = 0;

    /* High-quality scaling when shrinking 1920×1080 → 854×480. */
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    let nextEventIdx = 0;
    const replayStart = performance.now();
    let rafId = 0;

    const renderFrame = () => {
      const elapsed = performance.now() - replayStart;

      /* Dispatch every event whose replay time has come up — even if many
         arrive in the same frame, we must not skip any or the trajectory
         will be straight-lined between non-adjacent points.               */
      while (nextEventIdx < events.length && events[nextEventIdx].replayT <= elapsed) {
        const evt = events[nextEventIdx];
        const synthEvent = new PointerEvent('pointer' + evt.type, {
          bubbles: true, cancelable: true, button: 0,
          clientX: evt.x, clientY: evt.y, pointerType: 'mouse',
        });
        synthEvent.__opqSynthetic = true;
        /* WebGL canvas only exists on hero; on the board use the paint SVG
           or section root.  document-level paint listeners catch the bubble. */
        const target = webglCanvas || svg || root;
        target.dispatchEvent(synthEvent);
        nextEventIdx++;
      }

      /* Composite — use the WebGL chrome behind paint when present, else
         fill with the section's own background colour (e.g. white on the
         board). */
      if (webglCanvas) {
        ctx.fillStyle = '#000';
        ctx.fillRect(0, 0, W, H);
        try { ctx.drawImage(webglCanvas, 0, 0, W, H); } catch (_) {}
      } else {
        ctx.fillStyle = compositeBgColor;
        ctx.fillRect(0, 0, W, H);
      }

      /* SVG strokes — neon glow: wide halo + near halo + sharp core */
      ctx.save();
      ctx.scale(sx, sy);
      svg.querySelectorAll('path').forEach((p) => {
        const d = p.getAttribute('d');
        if (!d) return;
        try {
          const path2d = new Path2D(d);
          const color = getComputedStyle(p).stroke || '#fff';
          const w = parseFloat(p.getAttribute('stroke-width')) || 4;
          ctx.strokeStyle = color;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.lineWidth = w;

          ctx.shadowColor = color;
          ctx.shadowBlur = 18;
          ctx.stroke(path2d);
          ctx.stroke(path2d);          /* double-pass to brighten outer halo */
          ctx.shadowBlur = 7;
          ctx.stroke(path2d);          /* near halo */
          ctx.shadowColor = 'transparent';
          ctx.shadowBlur = 0;
          ctx.stroke(path2d);          /* sharp core */
        } catch (_) {}
      });
      ctx.restore();

      drawOverlayUI(ctx, root, sx, sy);

      const tailMs = 800;  // small grace period after last event for fluid to settle
      if (nextEventIdx < events.length || elapsed < totalDuration + tailMs) {
        rafId = requestAnimationFrame(renderFrame);
      } else {
        recorder.stop();
      }
    };

    recorder.onstop = () => {
      removeInputLock();
      const blob = new Blob(chunks, { type: 'video/webm' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `opq-paint-${Date.now()}.webm`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      setBusy(false);
    };

    recorder.start();
    rafId = requestAnimationFrame(renderFrame);
  };

  exportBtn.addEventListener('click', runExport);
}

/* PNG image export — single-frame composite of the section's current
   visual state (paint strokes + UI overlay).  Triggered by the dock
   button with [data-opq-ctrl="image"]. */
function initImageExport(root) {
  const imageBtn = root.querySelector('[data-opq-ctrl="image"]');
  const webglCanvas = root.querySelector('[data-opq-canvas]');
  const svg = root.querySelector('[data-opq-paint-svg]');
  if (!imageBtn || !svg) return;

  imageBtn.addEventListener('click', () => {
    /* Larger output than video (1920x1080) so the saved image stays sharp. */
    const W = 1920, H = 1080;
    const c = document.createElement('canvas');
    c.width = W; c.height = H;
    const ctx = c.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const sx = W / window.innerWidth;
    const sy = H / window.innerHeight;
    const bg = getComputedStyle(root).backgroundColor || '#ffffff';

    if (webglCanvas) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, W, H);
      try { ctx.drawImage(webglCanvas, 0, 0, W, H); } catch (_) {}
    } else {
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
    }

    /* SVG strokes — neon stack identical to video composite */
    ctx.save();
    ctx.scale(sx, sy);
    svg.querySelectorAll('path').forEach((p) => {
      const d = p.getAttribute('d');
      if (!d) return;
      try {
        const path2d = new Path2D(d);
        const color = getComputedStyle(p).stroke || '#000';
        const w = parseFloat(p.getAttribute('stroke-width')) || 4;
        ctx.strokeStyle = color;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = w;
        ctx.shadowColor = color;
        ctx.shadowBlur = 18;
        ctx.stroke(path2d);
        ctx.stroke(path2d);
        ctx.shadowBlur = 7;
        ctx.stroke(path2d);
        ctx.shadowColor = 'transparent';
        ctx.shadowBlur = 0;
        ctx.stroke(path2d);
      } catch (_) {}
    });
    ctx.restore();

    /* Compose UI overlay (logo, status, meta, footer-nav, MENU, copyright)
       so the saved image is a faithful capture of the live page. */
    drawOverlayUI(ctx, root, sx, sy);

    c.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `opq-board-${Date.now()}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }, 'image/png');
  });
}

function initEdgePeeker(el, edge /* 'top' | 'bottom' */) {
  if (!el) return;
  const TRIGGER_PX = 140;
  const HIDE_DELAY = 420;
  let hideTimer;

  const show = () => {
    clearTimeout(hideTimer);
    el.classList.add('is-visible');
  };

  const hideSoon = () => {
    if (el.classList.contains('is-locked')) return;
    clearTimeout(hideTimer);
    hideTimer = setTimeout(() => el.classList.remove('is-visible'), HIDE_DELAY);
  };

  window.addEventListener('pointermove', (e) => {
    const dist = edge === 'top' ? e.clientY : window.innerHeight - e.clientY;
    if (dist <= TRIGGER_PX) show();
    else hideSoon();
  }, { passive: true });

  el.addEventListener('pointerenter', show);
  el.addEventListener('pointerleave', hideSoon);
}

function initDock(root) {
  initEdgePeeker(root.querySelector('[data-opq-dock]'), 'bottom');
}

function initNavPeek(root) {
  initEdgePeeker(root.querySelector('[data-opq-nav]'), 'top');
}

/* Discoverability hint on page load: briefly slide nav (top) and dock
   (bottom) into view, then auto-retract so the user knows hidden menus
   live at both edges. */
function initEdgeHints(root) {
  const nav  = root.querySelector('[data-opq-nav]');
  const dock = root.querySelector('[data-opq-dock]');
  if (!nav && !dock) return;

  const SHOW_DELAY = 450;   // wait for fluid sim to start + reduce flash
  const HOLD_TIME  = 1800;  // how long the menus stay visible

  setTimeout(() => {
    nav?.classList.add('is-visible');
    dock?.classList.add('is-visible');
    setTimeout(() => {
      nav?.classList.remove('is-visible');
      dock?.classList.remove('is-visible');
    }, HOLD_TIME);
  }, SHOW_DELAY);
}

function initLiveCoords(root) {
  const coordsEl = root.querySelector('[data-opq-coords]');
  if (!coordsEl) return;

  const pad = (n) => String(Math.max(0, Math.round(n))).padStart(4, '0');

  const update = (e) => {
    coordsEl.textContent = `X ${pad(e.clientX)}  Y ${pad(e.clientY)}`;
  };

  window.addEventListener('pointermove', update, { passive: true });
}

function initLiveTime(root) {
  const dateEl = root.querySelector('[data-opq-date]');
  const timeEl = root.querySelector('[data-opq-time]');
  if (!dateEl && !timeEl) return;

  const tz = root.dataset.timezone || 'Asia/Taipei';

  const dateFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });

  const timeFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  const update = () => {
    const now = new Date();
    if (dateEl) dateEl.textContent = dateFormatter.format(now).replace(/-/g, '.');
    if (timeEl) timeEl.textContent = timeFormatter.format(now);
  };

  update();
  setInterval(update, 1000);
}

function getDisplacementMap({ height, width, radius, depth }) {
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg height="${height}" width="${width}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <style>.mix { mix-blend-mode: screen; }</style>
      <defs>
        <linearGradient id="Y" x1="0" x2="0"
          y1="${Math.ceil((radius / height) * 15)}%"
          y2="${Math.floor(100 - (radius / height) * 15)}%">
          <stop offset="0%" stop-color="#0F0"/><stop offset="100%" stop-color="#000"/>
        </linearGradient>
        <linearGradient id="X"
          x1="${Math.ceil((radius / width) * 15)}%"
          x2="${Math.floor(100 - (radius / width) * 15)}%"
          y1="0" y2="0">
          <stop offset="0%" stop-color="#F00"/><stop offset="100%" stop-color="#000"/>
        </linearGradient>
      </defs>
      <rect x="0" y="0" height="${height}" width="${width}" fill="#808080"/>
      <g filter="blur(2px)">
        <rect x="0" y="0" height="${height}" width="${width}" fill="#000080"/>
        <rect x="0" y="0" height="${height}" width="${width}" fill="url(#Y)" class="mix"/>
        <rect x="0" y="0" height="${height}" width="${width}" fill="url(#X)" class="mix"/>
        <rect x="${depth}" y="${depth}"
          height="${height - 2 * depth}" width="${width - 2 * depth}"
          fill="#808080" rx="${radius}" ry="${radius}"
          filter="blur(${depth}px)"/>
      </g>
    </svg>`
  );
}

function getDisplacementFilter({ height, width, radius, depth, strength = 100, chromaticAberration = 0 }) {
  const map = getDisplacementMap({ height, width, radius, depth });
  return 'data:image/svg+xml;utf8,' + encodeURIComponent(
    `<svg height="${height}" width="${width}" viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="displace" color-interpolation-filters="sRGB">
          <feImage x="0" y="0" height="${height}" width="${width}" href="${map}" result="dm"/>
          <feDisplacementMap in="SourceGraphic" in2="dm"
            scale="${strength + chromaticAberration * 2}"
            xChannelSelector="R" yChannelSelector="G"/>
          <feColorMatrix type="matrix"
            values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="dR"/>
          <feDisplacementMap in="SourceGraphic" in2="dm"
            scale="${strength + chromaticAberration}"
            xChannelSelector="R" yChannelSelector="G"/>
          <feColorMatrix type="matrix"
            values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
            result="dG"/>
          <feDisplacementMap in="SourceGraphic" in2="dm"
            scale="${strength}"
            xChannelSelector="R" yChannelSelector="G"/>
          <feColorMatrix type="matrix"
            values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
            result="dB"/>
          <feBlend in="dR" in2="dG" mode="screen"/>
          <feBlend in2="dB" mode="screen"/>
        </filter>
      </defs>
    </svg>`
  ) + '#displace';
}

const SUPPORTS_BACKDROP_URL = (() => {
  const t = document.createElement('div');
  t.style.cssText = 'backdrop-filter: url(#x)';
  return t.style.backdropFilter === 'url(#x)' || t.style.backdropFilter === 'url("#x")';
})();

function applyLiquidGlass(el) {
  const opts = {
    blur:                parseFloat(el.dataset.lgBlur)     || 2,
    depth:               parseFloat(el.dataset.lgDepth)    || 6,
    strength:            parseFloat(el.dataset.lgStrength) || 60,
    chromaticAberration: parseFloat(el.dataset.lgCab)      || 3,
    brightness:          parseFloat(el.dataset.lgBrightness) || 1.05,
    saturate:            parseFloat(el.dataset.lgSaturate)   || 1.3,
  };

  const rect = el.getBoundingClientRect();
  const width  = Math.max(1, Math.round(rect.width));
  const height = Math.max(1, Math.round(rect.height));
  const radius = parseFloat(getComputedStyle(el).borderRadius) || 0;

  if (!SUPPORTS_BACKDROP_URL) {
    el.style.backdropFilter = `blur(${opts.blur + 8}px) saturate(180%)`;
    el.style.webkitBackdropFilter = `blur(${opts.blur + 8}px) saturate(180%)`;
    return;
  }

  const filter = getDisplacementFilter({
    height, width, radius,
    depth: opts.depth,
    strength: opts.strength,
    chromaticAberration: opts.chromaticAberration,
  });

  el.style.backdropFilter =
    `blur(${opts.blur / 2}px) url('${filter}') blur(${opts.blur}px) brightness(${opts.brightness}) saturate(${opts.saturate})`;
}

function initLiquidGlass(root) {
  root.querySelectorAll('.liquid-glass').forEach((el) => {
    applyLiquidGlass(el);
    const ro = new ResizeObserver(() => applyLiquidGlass(el));
    ro.observe(el);
  });
}

/* Negative-film cursor spotlight — any section can opt in by including an
   element with [data-opq-cursor-spotlight].  The element should have its
   own visual style (mix-blend-mode: difference is the typical choice). */
function initCursorSpotlight(root) {
  const mask = root.querySelector('[data-opq-cursor-spotlight]');
  if (!mask) return;
  let raf = 0, x = 0, y = 0;
  const update = () => {
    raf = 0;
    mask.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
  };
  window.addEventListener('pointermove', (e) => {
    x = e.clientX;
    y = e.clientY;
    mask.classList.add('is-active');
    if (!raf) raf = requestAnimationFrame(update);
  }, { passive: true });
  const hide = () => mask.classList.remove('is-active');
  window.addEventListener('pointerleave', hide);
  document.addEventListener('mouseleave', hide);
}

/* Strip whitespace-only / <br>-only paragraphs from article rich text so
   the 2-col grid doesn't get broken by Shopify's invisible spacers. */
function pruneEmptyArticleParagraphs(root) {
  const content = root.querySelector('.opq-article__content');
  if (!content) return;
  Array.from(content.children).forEach((el) => {
    if (el.tagName !== 'P') return;
    if (el.querySelector('img, video, iframe')) return;
    const text = (el.textContent || '').replace(/ /g, ' ').trim();
    if (text === '') {
      el.classList.add('opq-empty');
    }
  });
}

/* Shopify dev's `auctor` lorem-ipsum article ships with a Rolls-Royce shot
   as the featured image and a workshop sculpture as the first body image.
   For preview purposes we want the workshop image as the hero and the
   Rolls-Royce gone (it's redundant in this position). */
function fixAuctorImageOrder(root) {
  if (!/\/blogs\/[^/]+\/auctor(?:[/?#]|$)/.test(window.location.pathname)) return;
  const heroImg = root.querySelector('.opq-article__hero img');
  const content = root.querySelector('.opq-article__content');
  if (!heroImg || !content) return;
  const firstBodyImg = content.querySelector('img');
  if (!firstBodyImg) return;

  ['src', 'srcset', 'width', 'height', 'alt'].forEach((attr) => {
    const v = firstBodyImg.getAttribute(attr);
    if (v !== null) heroImg.setAttribute(attr, v);
    else heroImg.removeAttribute(attr);
  });

  let block = firstBodyImg;
  while (block.parentElement && block.parentElement !== content) {
    block = block.parentElement;
  }
  if (block && block.parentElement === content) block.remove();
}

/* Shared top-header init runs on every section that includes the
   `opq-top-header` snippet (hero, archive, about, ...).  Each section root
   has the data-opq-page attribute. */
document.querySelectorAll('[data-opq-page]').forEach((root) => {
  initLiveTime(root);
  initLiveCoords(root);
  initLiquidGlass(root);
  initNavPeek(root);
  initEdgeHints(root);
  initMenu(root);
  pruneEmptyArticleParagraphs(root);
  fixAuctorImageOrder(root);
  initCursorSpotlight(root);
});

/* MENU modal — full-screen frosted-glass overlay opened by the MENU button.
   Close on: close button, ESC, backdrop click, any nav link click. */
function initMenu(root) {
  const btn      = root.querySelector('[data-opq-menu]');
  const modal    = root.querySelector('[data-opq-menu-modal]');
  const closeBtn = root.querySelector('[data-opq-menu-close]');
  if (!btn || !modal) return;

  let prevBodyOverflow = '';

  const open = () => {
    modal.classList.add('is-open');
    modal.setAttribute('aria-hidden', 'false');
    prevBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    btn.setAttribute('aria-expanded', 'true');
  };

  const close = () => {
    modal.classList.remove('is-open');
    modal.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = prevBodyOverflow;
    btn.setAttribute('aria-expanded', 'false');
  };

  const isOpen = () => modal.classList.contains('is-open');

  btn.addEventListener('click', () => isOpen() ? close() : open());
  closeBtn?.addEventListener('click', close);

  /* ESC closes from anywhere in the document */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && isOpen()) close();
  });

  /* Backdrop click closes — but clicks on inner content shouldn't bubble
     up if they hit the modal background only */
  modal.addEventListener('click', (e) => {
    if (e.target === modal) close();
  });

  /* Any link in the menu closes (allowing the navigation to proceed) */
  modal.querySelectorAll('a').forEach((a) => {
    a.addEventListener('click', () => close());
  });
}

/* Hero-specific init: shader, paint canvas, dock controls, export.
   Only runs on the homepage section. */
document.querySelectorAll('[data-opq-hero]').forEach((root) => {
  try {
    root.__opqHero = new OpqHero(root);
  } catch (err) {
    console.error('[opq-hero] shader init failed', err);
  }
  initControls(root);
  initDock(root);
  initPaint(root);
  initExport(root);
  initImageExport(root);
});

/* Board-specific init: paint + dock + image / video export, but no shader,
   so it runs on a plain (white or theme-coloured) BG.  Procreate-like. */
document.querySelectorAll('[data-opq-board]').forEach((root) => {
  initControls(root);
  initDock(root);
  initPaint(root);
  initExport(root);
  initImageExport(root);
});
